import { LineItemType } from "legacyComponents/LineItemContainer.types";
import { type MutableRefObject } from "react";

import { calculateBuilderCost } from "utilities/lineItem/lineItemHelper";

import { type RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { type IPreviewLineItemData } from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import {
    type IGridLineItemActionRow,
    type ILineItemGroup,
    type ILineItemRow,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    type ICostLineItem,
    type IMarkupLineItem,
    isLineItemActionRow,
    isLineItemGroupRow,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { type ICostItemLineItem } from "entity/CostGroup/CostGroup.api.types";
import {
    type IEstimateLineItem,
    NewEntityId,
    type WorksheetLineItem,
} from "entity/estimate/common/estimate.common.types";

/**
 * Given an array of Grid format items, return true if the grid data contains at least 2 format items
 * that are "sortable". That is data that contains either:
 * - At least two "top level" format items
 * - At least two "child" format items
 */
export const hasMoreThanOneOrderableFormatItems = (
    formatItems: (ILineItemRow | ILineItemGroup | IGridLineItemActionRow)[],
) => {
    let numberOfOrderableTopLevelFormatItems = 0;
    let numberOfOrderableChildFormatItems = 0;
    for (const formatItem of formatItems) {
        if (!isLineItemActionRow(formatItem)) {
            // Add to count for the top level line item
            if (++numberOfOrderableTopLevelFormatItems > 1) {
                return true;
            }

            // For line item groups, be sure to include the children elements
            if (isLineItemGroupRow(formatItem)) {
                for (const childFormatItem of formatItem.items) {
                    if (!isLineItemActionRow(childFormatItem)) {
                        if (++numberOfOrderableChildFormatItems > 1) {
                            return true;
                        }

                        // If it is a subcategory, we need to include its children elements
                        if (isLineItemGroupRow(childFormatItem)) {
                            for (const subCatLineItem of childFormatItem.items) {
                                if (!isLineItemActionRow(subCatLineItem)) {
                                    if (++numberOfOrderableChildFormatItems > 1) {
                                        return true;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    return false;
};

export const copyCostItemToWorksheetItem = (
    itemToReplace: WorksheetLineItem,
    costItem: ICostItemLineItem,
): WorksheetLineItem => {
    const lineItemToReplace = copyCostItemToMarkupLineItem<WorksheetLineItem>(
        itemToReplace,
        costItem,
    );

    lineItemToReplace.costCodeTitle = costItem.costCodeTitle;
    // Preserve the original format parent — the catalog item's parentId is a
    // catalog cost-group ID, not a proposal format item ID. The backend will
    // update the format parent if the cost code category changed.
    lineItemToReplace.parentId = itemToReplace.parentId;

    return lineItemToReplace;
};

export const duplicateWorksheetLineItem = (
    lineItemToDuplicate: WorksheetLineItem,
): WorksheetLineItem => {
    const duplicateLineItem = { ...lineItemToDuplicate };
    duplicateLineItem.id = 0;
    duplicateLineItem.lineItemType = LineItemType.EstimateLineItem;
    duplicateLineItem.takeoffLineItemId = undefined;
    duplicateLineItem.assemblyId = null;
    duplicateLineItem.relatedItems = null;
    duplicateLineItem.hasRelatedPurchaseOrder = false;
    duplicateLineItem.relatedPurchaseOrderLineItemId = null;
    duplicateLineItem.isEditable = true;
    duplicateLineItem._clonedFromId = lineItemToDuplicate.id;

    return duplicateLineItem;
};

function copyCostItemToMarkupLineItem<T extends IMarkupLineItem>(
    itemToReplace: T,
    costItem: ICostItemLineItem,
): T {
    let lineItemToReplace = { ...itemToReplace };

    lineItemToReplace = copyCostItemToCostLineItem(itemToReplace, costItem);

    if (costItem.markupIsLinked) {
        lineItemToReplace.ownerPrice = costItem.ownerPrice;
        lineItemToReplace.markupAmount = costItem.markupAmount;
        lineItemToReplace.markupPercent = costItem.markupPercent;
        lineItemToReplace.margin = costItem.margin;
        lineItemToReplace.markupPerUnit = costItem.markupPerUnit;
        lineItemToReplace.markupType = costItem.markupType;
    }

    return lineItemToReplace;
}

export function copyCostItemToCostLineItem<T extends ICostLineItem>(
    itemToReplace: T,
    costItem: ICostItemLineItem,
): T {
    const lineItemToReplace = { ...itemToReplace };

    lineItemToReplace.costItemId = costItem.costItemId;
    lineItemToReplace.itemTitle = costItem.itemTitle;
    lineItemToReplace.unitCost = costItem.unitCost;
    lineItemToReplace.unit = costItem.unit;
    lineItemToReplace.costCodeId = costItem.costCodeId;
    lineItemToReplace.parentId = costItem.parentId;

    lineItemToReplace.description = costItem.description;
    lineItemToReplace.internalNotes = costItem.internalNotes;

    if (costItem.quantityIsLinked) {
        lineItemToReplace.quantity = costItem.quantity;
    }

    lineItemToReplace.builderCost = calculateBuilderCost(
        lineItemToReplace.unitCost,
        lineItemToReplace.quantity,
    );

    lineItemToReplace.costTypes = costItem.costTypes;

    lineItemToReplace.vendorType = costItem.vendorType;
    lineItemToReplace.vendorProductId = costItem.vendorProductId;
    lineItemToReplace.productLink = costItem.productLink;
    lineItemToReplace.isDiscontinued = costItem.isDiscontinued;

    return lineItemToReplace;
}

export function areWorksheetItemsEqual(itemA: WorksheetLineItem, itemB: WorksheetLineItem) {
    return (
        itemA.itemTitle === itemB.itemTitle &&
        itemA.costCodeId === itemB.costCodeId &&
        itemA.costCodeTitle === itemB.costCodeTitle &&
        itemA.description === itemB.description &&
        itemA.internalNotes === itemB.internalNotes &&
        itemA.unitCost === itemB.unitCost &&
        itemA.unit === itemB.unit &&
        itemA.quantity === itemB.quantity &&
        itemA.markupType === itemB.markupType &&
        itemA.markupPercent === itemB.markupPercent &&
        itemA.markupPerUnit === itemB.markupPerUnit &&
        itemA.markupAmount === itemB.markupAmount &&
        itemA.ownerPrice === itemB.ownerPrice &&
        itemA.builderCost === itemB.builderCost &&
        itemA.costTypes?.every((c) => itemB.costTypes?.includes(c)) &&
        itemB.costTypes?.every((c) => itemA.costTypes?.includes(c))
    );
}

export function copyPreviewLineItemDataToEstimateLineItem<T extends IEstimateLineItem>(
    lineItem: IPreviewLineItemData,
    lineItemToReplace: T,
) {
    const newLineItem = { ...lineItemToReplace };

    newLineItem.costItemId = lineItem.costItemId;
    newLineItem.itemTitle = lineItem.title;
    newLineItem.costCodeId = lineItem.costCodeId;
    newLineItem.description = lineItem.description;
    newLineItem.internalNotes = lineItem.internalNotes;
    newLineItem.unitCost = lineItem.unitCost;
    newLineItem.quantity = lineItem.quantity;
    newLineItem.unit = lineItem.unitType;

    newLineItem.markupType = lineItem.markupType;
    newLineItem.markupPerUnit = lineItem.markupPerUnit;
    newLineItem.markupPercent = lineItem.markupPercentage;
    newLineItem.markupAmount = lineItem.markupAmount;
    newLineItem.margin = lineItem.margin;

    newLineItem.ownerPrice = lineItem.ownerPrice;
    newLineItem.builderCost = lineItem.builderCost;
    newLineItem.costTypes = lineItem.costTypes;
    newLineItem.markedAs = lineItem.markedAs;

    return newLineItem;
}

export function getRelatedEntityPermissionsRecord(
    entitiesWithViewPermissions: RelatedItemType[],
): Record<RelatedItemType, boolean> {
    const permissionsRecord: Record<RelatedItemType, boolean> = {} as Record<
        RelatedItemType,
        boolean
    >;
    entitiesWithViewPermissions.forEach((item) => {
        permissionsRecord[item] = true;
    });
    return permissionsRecord;
}

export const firstNewLineItemIdValue = NewEntityId;

/**
 * Ensures the new-line-item counter is strictly below the smallest existing id in
 * the list. Frontend-generated temp ids are always negative and decremented per add;
 * loaded items (e.g. backend-generated temp ids on a default progress invoice) can
 * also be negative, so we push the counter below the existing min to prevent collisions.
 *
 * Idempotent: safe to call on every line-items change — the ref only moves down.
 */
export const advanceNewLineItemIdRefBeyondExisting = (
    ref: MutableRefObject<number>,
    lineItems: readonly { id: number }[],
): void => {
    if (lineItems.length === 0) {
        return;
    }
    const minId = Math.min(...lineItems.map((item) => item.id));
    if (minId <= ref.current) {
        ref.current = minId - 1;
    }
};
